<template>
  <div v-if="employeeRequest" class="forms-elements">
    <Breadcrumbs v-bind:breadcrumbs="breadcrumbs" />

    <Widget v-if="employeeRequest">
      <b-row>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>Antragsdetails </strong>
          </legend>
          <template v-if="isAdmin">
            <b-form-group
              horizontal
              :label="$t('employee')"
              label-for="user"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <div class="position-relative">
                <multiselect
                  class="multiselect-user"
                  :class="{ 'is-invalid': validation.touched.user && validation.errors.user }"
                  v-model="employeeRequest.user"
                  :options="filteredUsers"
                  track-by="id"
                  v-on:search-change="onFilterUsers"
                  @close="validateField('user')"
                  @input="validateField('user')"
                  :filter="true"
                  :internal-search="false"
                  :clear-on-select="false"
                  :multiple="false"
                  :show-labels="false"
                  :allow-empty="true"
                  :deselectLabel="$t('deselectLabel')"
                  selectLabel=""
                  selectedLabel=""
                  :placeholder="'Mitarbeiter ' + $t('select') + '/suchen'"
                >
                  <template slot="singleLabel" slot-scope="{ option }">
                    {{ option.firstname }} {{ option.lastname }}
                  </template>
                  <template slot="option" slot-scope="{ option }">
                    {{ option.firstname }} {{ option.lastname }}
                  </template>
                  <template slot="tag" slot-scope="{ option }">
                    {{ option.firstname }} {{ option.lastname }}
                  </template>
                </multiselect>
                <small v-if="validation.touched.user && validation.errors.user" class="text-danger">
                  {{ validation.errors.user }}
                </small>
              </div>
            </b-form-group>
          </template>

          <b-form-group
            horizontal
            :label="$t('type')"
            label-for="employeeRequestType"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="position-relative">
              <multiselect
                :placeholder="$t('employeeRequestType')"
                :class="{
                  'is-invalid':
                    validation.touched.employeeRequestType && validation.errors.employeeRequestType,
                }"
                v-model="employeeRequest.employeeRequestType"
                id="employeeRequestType"
                :options="getEnumValues('EmployeeRequestType')"
                @close="validateField('employeeRequestType')"
                @input="validateField('employeeRequestType')"
                :allow-empty="false"
                :show-labels="false"
                deselectLabel
              >
                <template slot="singleLabel" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
                <template slot="option" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
              </multiselect>
              <small
                v-if="
                  validation.touched.employeeRequestType && validation.errors.employeeRequestType
                "
                class="text-danger"
              >
                {{ validation.errors.employeeRequestType }}
              </small>
            </div>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('installationStartAt_short')"
            label-for="startTime"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="calendar-wrapper">
              <div class="position-relative">
                <Calendar
                  v-model="startTime"
                  :class="{
                    'is-invalid': validation.touched.startTime && validation.errors.startTime,
                  }"
                  dateFormat="dd.mm.yy"
                  :placeholder="$t('installationStartAt_short')"
                  locale="de"
                  :showWeek="true"
                  :yearNavigator="true"
                  :monthNavigator="true"
                  yearRange="2025:2040"
                  class="full-width-calendar"
                  @date-select="validateField('startTime')"
                  @close="validateField('startTime')"
                >
                </Calendar>
                <small
                  v-if="validation.touched.startTime && validation.errors.startTime"
                  class="text-danger"
                >
                  {{ validation.errors.startTime }}
                </small>
              </div>
            </div>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('end')"
            label-for="stopTime"
            label-class="text-md-right"
            :label-cols="4"
            breakpoint="md"
          >
            <div class="calendar-wrapper">
              <div class="position-relative">
                <Calendar
                  v-model="stopTime"
                  :class="{
                    'is-invalid': validation.touched.stopTime && validation.errors.stopTime,
                  }"
                  dateFormat="dd.mm.yy"
                  :placeholder="$t('end')"
                  locale="de"
                  :showWeek="true"
                  :yearNavigator="true"
                  :monthNavigator="true"
                  yearRange="1930:2030"
                  class="full-width-calendar"
                  @date-select="validateField('stopTime')"
                  @close="validateField('stopTime')"
                >
                </Calendar>
                <small
                  v-if="validation.touched.stopTime && validation.errors.stopTime"
                  class="text-danger"
                >
                  {{ validation.errors.stopTime }}
                </small>
              </div>
            </div>
          </b-form-group>
        </b-col>
        <b-col lg="6" md="6" sm="12">
          <legend>
            <strong>Beschreibung</strong>
          </legend>

          <b-form-textarea
            v-model="employeeRequest.description"
            placeholder="Beschreibung eingeben..."
            rows="5"
          ></b-form-textarea>
        </b-col>
        <b-col lg="6" md="6" sm="12" v-if="!isNew">
          <legend>
            <strong>Genehmigung</strong>
          </legend>

          <VerificationState
            :employeeRequest="employeeRequest"
            verificationStateType="employeeRequestState"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="12" md="12" sm="12">
          <ButtonBar
            :showPreviousButton="buttons.showPreviousButton"
            :showNextButton="buttons.showNextButton"
            :showDeleteButton="buttons.showDeleteButton"
            :model="employeeRequest"
            modelEditRoute="EmployeeRequestEditPage"
            :modelListRoute="localSourcePage"
            modelRouteParamName="employeeRequestNumber"
            :nextModelNumber="employeeRequest.nextEmployeeRequestNumber"
            :previousModelNumber="employeeRequest.previousEmployeeRequestNumber"
            :updateCallback="updateWithValidation"
            :createCallback="createWithValidation"
            :deleteCallback="deleteEmployeeRequest"
            :fetchCallback="fetchEmployeeRequestsPaginated"
          />
        </b-col>
      </b-row>
    </Widget>
  </div>
</template>

<script>
import Widget from '@/components/Widget/Widget';
import Breadcrumbs from '@/components/Breadcrumbs';
import { mapGetters, mapActions } from 'vuex';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import Multiselect from 'vue-multiselect';
import ButtonBar from '@/components/ButtonBar.vue';
import Calendar from 'primevue/calendar';
import VerificationState from '@/components/VerificationState.vue';

export default {
  name: 'EmployeeRequestPage',
  components: {
    Widget,
    Breadcrumbs,
    Multiselect,
    ButtonBar,
    Calendar,
    VerificationState,
  },
  props: {
    employeeRequestNumber: {
      required: false,
      default: null,
    },
    sourcePage: {
      type: String,
      default: 'EmployeeRequestPage',
    },
  },
  data() {
    return {
      number: null,
      buttonsDisabled: false,
      editor: InlineEditor,
      editorConfig: {},
      filteredUsers: [],
      localSourcePage: this.sourcePage,
      validation: {
        errors: {},
        touched: {
          user: false,
          employeeRequestType: false,
          startTime: false,
          stopTime: false,
        },
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'getCurrentUser', 'isClient']),
    ...mapGetters([
      'getEmployeeRequest',
      'getEmployeeRequestByNumber',
      'getEmployeeRequestCount',
      'getEnumValues',
      'getUsers',
    ]),
    user() {
      return this.getCurrentUser ? this.getUser(this.getCurrentUser.id) : null;
    },
    startTime: {
      get() {
        return this.$calendarFormat(this.employeeRequest.startTime);
      },
      set(value) {
        this.employeeRequest.startTime = value;
      },
    },
    stopTime: {
      get() {
        return this.$calendarFormat(this.employeeRequest.stopTime);
      },
      set(value) {
        this.employeeRequest.stopTime = value;
      },
    },
    users() {
      return this.$store.getters.getUsers || [];
    },
    buttons() {
      return {
        showPreviousButton:
          this.employeeRequest && this.employeeRequest.previousEmployeeRequestNumber ? true : false,
        showNextButton:
          this.employeeRequest && this.employeeRequest.nextEmployeeRequestNumber ? true : false,
        showDeleteButton:
          this.employeeRequest && this.employeeRequest.number !== '_new' ? true : false,
      };
    },
    employeeRequest() {
      try {
        if (!this.number) return null;

        return this.$store.getters.getEmployeeRequestByNumber(this.number);
      } catch (error) {
        return null;
      }
    },
    isNew() {
      return this.employeeRequest && this.employeeRequest._id === '_new';
    },
    breadcrumbs() {
      return [
        { name: 'Home', route: { name: 'home' } },
        { name: this.$t('employeeRequest'), route: { name: 'EmployeeRequestPage' } },
        {
          name:
            this.employeeRequest && this.employeeRequest.number !== '_new'
              ? this.$t('edit')
              : this.$t('new'),
        },
      ];
    },
  },
  methods: {
    ...mapActions([
      'initEmployeeRequest',
      'fetchEmployeeRequestsPaginated',
      'fetchEmployeeRequestByNumber',
      'updateEmployeeRequest',
      'createEmployeeRequest',
      'deleteEmployeeRequest',
      'fetchEnumValues',
    ]),

    initializeValidation() {
      if (this.employeeRequest && this.employeeRequest.number !== '_new') {
        this.validation.errors = {
          user: null,
          employeeRequestType: null,
          startTime: null,
          stopTime: null,
        };

        this.validation.touched = {
          user: false,
          employeeRequestType: false,
          startTime: false,
          stopTime: false,
        };

        if (this.isAdmin && this.employeeRequest.user && this.employeeRequest.user.number) {
          const userExists = this.filteredUsers.some(
            (u) => u.number === this.employeeRequest.user.number
          );

          if (!userExists) {
            this.filteredUsers.push(this.employeeRequest.user);
          }
        }
      }
    },
    validateField(field) {
      this.validation.touched[field] = true;

      switch (field) {
        case 'user':
          this.validation.errors.user =
            !this.employeeRequest.user || !this.employeeRequest.user.id
              ? 'Bitte wählen Sie einen Mitarbeiter aus'
              : null;
          break;
        case 'employeeRequestType':
          this.validation.errors.employeeRequestType = !this.employeeRequest.employeeRequestType
            ? 'Bitte wählen Sie einen Antragstyp aus'
            : null;
          break;
        case 'startTime':
          this.validation.errors.startTime = !this.employeeRequest.startTime
            ? 'Bitte wählen Sie ein Startdatum aus'
            : null;
          if (this.employeeRequest.startTime && this.employeeRequest.stopTime) {
            this.validateDateRange();
          }
          break;
        case 'stopTime':
          this.validation.errors.stopTime = !this.employeeRequest.stopTime
            ? 'Bitte wählen Sie ein Enddatum aus'
            : null;
          if (this.employeeRequest.startTime && this.employeeRequest.stopTime) {
            this.validateDateRange();
          }
          break;
      }
    },

    validateDateRange() {
      const startDate = new Date(this.employeeRequest.startTime);
      const endDate = new Date(this.employeeRequest.stopTime);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < startDate) {
        this.validation.errors.stopTime = 'Das Enddatum darf nicht vor dem Startdatum liegen';
      } else {
        if (
          this.validation.errors.stopTime === 'Das Enddatum darf nicht vor dem Startdatum liegen'
        ) {
          this.validation.errors.stopTime = null;
        }
      }
    },

    validateForm() {
      Object.keys(this.validation.touched).forEach((field) => {
        this.validation.touched[field] = true;
      });

      this.validateField('user');
      this.validateField('employeeRequestType');
      this.validateField('startTime');
      this.validateField('stopTime');

      if (this.employeeRequest.startTime && this.employeeRequest.stopTime) {
        this.validateDateRange();
      }

      const hasErrors = Object.values(this.validation.errors).some((error) => error !== null);
      return !hasErrors;
    },

    async updateWithValidation() {
      if (this.validateForm()) {
        return this.updateEmployeeRequest(this.employeeRequest);
      }
      return Promise.reject('Validierungsfehler');
    },

    async createWithValidation() {
      if (this.validateForm()) {
        return this.createEmployeeRequest(this.employeeRequest);
      }
      return Promise.reject('Validierungsfehler');
    },

    async fetchUsers() {
      return this.$store.dispatch('fetchUsers');
    },

    async onSelectUser(user) {
      // console.log(user);
    },

    async onFilterUsers(query) {
      if (!this.isAdmin) return;

      query = query.toLowerCase();
      this.filteredUsers = this.getUsers.filter(
        (user) =>
          (user.firstname && user.firstname.toLowerCase().includes(query)) ||
          (user.lastname && user.lastname.toLowerCase().includes(query))
      );
    },

    onEditorReady(event) {},

    async setup() {
      await this.fetchEnumValues('EmployeeRequestState');
      await this.fetchEnumValues('EmployeeRequestType');
      if (this.isAdmin) {
        await this.fetchUsers();
      }
    },
  },
  async mounted() {
    await this.setup();

    // Only set filteredUsers if the current user is an admin
    if (this.isAdmin) {
      this.filteredUsers = this.getUsers;
    }

    this.number = this.employeeRequestNumber || '_new';

    if (this.employeeRequestNumber) {
      try {
        await this.$store.dispatch('fetchEmployeeRequestByNumber', this.number);
        this.$nextTick(() => {
          this.initializeValidation();
        });
      } catch (error) {
        // console.error('Error fetching employee request:', error);
      }
    } else {
      await this.$store.dispatch('initEmployeeRequest');

      if (!this.isAdmin && this.getCurrentUser && this.employeeRequest) {
        this.employeeRequest.user = this.getCurrentUser;
        this.validateField('user');
      }
    }

    if (this.$route.query.sourcePage) {
      this.localSourcePage = this.$route.query.sourcePage;
    }
  },

  async beforeRouteUpdate(to, from, next) {
    if (
      to.params.employeeRequestNumber &&
      to.params.employeeRequestNumber !== this.employeeRequestNumber
    ) {
      this.localSourcePage = to.query.sourcePage || 'EmployeeRequestPage';

      this.$store.commit('SET_EMPLOYEEREQUEST_DEFAULT_STATE');
      this.number = to.params.employeeRequestNumber;

      if (this.number === '_new') {
        await this.$store.dispatch('initEmployeeRequest');

        if (!this.isAdmin && this.getCurrentUser && this.employeeRequest) {
          this.employeeRequest.user = this.getCurrentUser;
          this.validateField('user');
        }

        next();
      } else {
        try {
          await this.$store.dispatch('fetchEmployeeRequestByNumber', this.number);
          await this.fetchEnumValues('EmployeeRequestState');
          await this.fetchEnumValues('EmployeeRequestType');

          if (this.isAdmin) {
            await this.fetchUsers();
          }

          this.$nextTick(() => {
            this.initializeValidation();
          });
          next();
        } catch (error) {
          // console.error('Error in route update:', error);
          next(false);
        }
      }
    } else {
      next();
    }
  },
};
</script>

<style scoped lang="scss">
.calendar-wrapper {
  width: 100%;
}

.full-width-calendar {
  width: 100%;
}

:deep(.p-calendar) {
  width: 100%;
}

:deep(.p-calendar .p-inputtext) {
  width: 100%;
}

:deep(.is-invalid) .multiselect__tags,
:deep(.is-invalid) .p-inputtext {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.text-danger {
  color: #dc3545;
  font-size: 80%;
  margin-top: 0.25rem;
  display: block;
}

.position-relative {
  position: relative;
}

.calendar-wrapper {
  margin-bottom: 1.5rem;
}

:deep(.is-invalid):focus .multiselect__tags,
:deep(.is-invalid):focus .p-inputtext {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

:deep(.is-invalid)::placeholder {
  color: #dc3545;
  opacity: 0.5;
}
</style>
